import { useEffect, useRef, useState } from "react";

import classNames from "classnames/bind";
import FAQ from "common/blocks/FAQ";
import { QaProps } from "common/blocks/FAQ/QA";
import Tabs from "common/blocks/Tabs";
import { TabsData } from "common/blocks/Tabs";
import TextInput from "common/blocks/TextInput";
import UIIcon from "common/blocks/UIIcon";
import dayjs from "dayjs";
import { VehicleDetailsProps } from "features/vehicleDetails/components/VehicleDetails";
import { OrderedSpec } from "features/vehicleDetails/components/VehicleDetails/components/CarSpecs";
import { VehicleFiguresProps } from "features/vehicleDetails/components/VehicleFigures";
import { useForm } from "react-hook-form";

import Wrapper from "../common/Wrapper";
import styles from "./FeaturesSpecs.module.scss";

const cx = classNames.bind(styles);

export type FeaturesSpecsSelectedTab = "specs" | "features";

export type FeaturesSpecsModalProps = {
    params: {
        vehicleFigureProps: VehicleFiguresProps["components"];
        vehicleDetailsProps: VehicleDetailsProps["components"];
        selectedTab?: FeaturesSpecsSelectedTab;
        selectedRow?: string;
    };
};

const FeaturesSpecsModal = ({ params }: FeaturesSpecsModalProps) => {
    const customOnClick = useRef<{ [key: string]: Function }>({});
    const [searchTerms, setSearchTerms] = useState<string[]>([]);
    const [openedItems, setOpenedItems] = useState<string[]>([]);
    const dirtyItemsRef = useRef(false);

    const { register, watch, setValue } = useForm<{ search: string | undefined }>({
        defaultValues: {
            search: undefined,
        },
    });

    const searchValue = watch("search");

    const handleResetSearch = () => setValue("search", undefined);

    useEffect(() => {
        if (params.selectedRow) {
            params.selectedRow &&
                customOnClick.current &&
                customOnClick.current?.[`${params.selectedRow}`]?.();
        }
    }, [params.selectedRow]);

    useEffect(() => {
        setSearchTerms(
            searchValue
                ? (searchValue
                      .toLowerCase()
                      .split(",")
                      .map((term) => term.trim()) ?? [])
                : []
        );
    }, [searchValue]);

    const {
        vehicleDetailsProps,
        vehicleFigureProps,
        selectedTab = "specs",
        selectedRow = "generalspecs",
    } = params;

    if (!vehicleDetailsProps || !vehicleFigureProps) return null;

    const tabNameDefaultSelected =
        selectedTab === "features"
            ? vehicleDetailsProps.carFeatures.title
            : vehicleDetailsProps.carSpecs.title;

    const carSpecsCategories: {
        rowTitle: string;
        data: Record<string, string>[];
        order: OrderedSpec[];
        orderedData: { key: string; value: string; postfix?: string }[];
        selectedRow: string;
        noSpecsAvailableMessage: string;
    }[] = Object.entries(
        params.vehicleDetailsProps.carSpecs.specs.vehicleSpecsSidePanel.specs
    )?.map((entry) => {
        const [rowTitle, data] = entry;

        // Dirty hack, but this modal is very messy... couldn't see another way to get this to work
        const noSpecsAvailableMessage =
            rowTitle == "Service history"
                ? params.vehicleFigureProps.serviceHistory.noDataText
                : "";

        return {
            rowTitle,
            data,
            order: [],
            orderedData: [],
            selectedRow: rowTitle,
            noSpecsAvailableMessage: noSpecsAvailableMessage,
        };
    });

    const carFeaturesCategories: {
        rowTitle: string;
        data: string[];
        order: OrderedSpec[];
        orderedData: { key: string; value: string; postfix?: string }[];
        selectedRow: string;
        noSpecsAvailableMessage: string;
    }[] = Object.entries(
        params.vehicleDetailsProps.carSpecs.specs.vehicleSpecsSidePanel.features
    )?.map((entry) => {
        const [rowTitle, data] = entry;

        return {
            rowTitle,
            data,
            order: [],
            orderedData: [],
            selectedRow: rowTitle,
            noSpecsAvailableMessage: ``,
        };
    });

    const orderedCarSpecsCategories = carSpecsCategories.map((unorderedCategory) => {
        let orderedData = unorderedCategory.order
            .map((item) => {
                const valueRow = unorderedCategory.data.find((dataRow) => !!dataRow[item.key]);
                const value = valueRow?.[0] ?? null;

                return {
                    key: item.label,
                    value,
                    postfix: item.postfix ?? "",
                };
            })
            .filter((item) => !!item.value);

        if (!orderedData.length && unorderedCategory?.selectedRow) {
            orderedData =
                unorderedCategory.data?.map((dataRow) => {
                    const [key, value] = Object.entries(dataRow)[0];
                    return {
                        key,
                        value,
                        postfix: unorderedCategory.rowTitle == "Service history" ? " miles" : "",
                    };
                }) ?? orderedData;
        }

        return {
            ...unorderedCategory,
            orderedData,
        };
    });

    const orderedCarFeaturesCategories = carFeaturesCategories.map((unorderedCategory) => {
        let orderedData = unorderedCategory.order
            .map((item) => {
                const valueRow = unorderedCategory.data.find((dataRow) => dataRow === item.label);
                const value = valueRow?.[0] ?? null;

                return {
                    key: item.label,
                    value,
                    postfix: item.postfix ?? "",
                };
            })
            .filter((item) => !!item.value);

        if (!orderedData.length && unorderedCategory?.selectedRow) {
            orderedData =
                unorderedCategory.data?.map((dataRow) => {
                    return {
                        key: dataRow,
                        value: dataRow,
                        postfix: "",
                    };
                }) ?? orderedData;
        }

        return {
            ...unorderedCategory,
            orderedData,
        };
    });
    const searchTermCombinedIsAtLeast2Chars = searchTerms.join().length >= 2;
    const showingCombined = !!searchTerms?.length && searchTermCombinedIsAtLeast2Chars;

    const highlight = cx("highlight");
    const regex = (terms: string[]) =>
        new RegExp(
            `(${terms.reduce((acc, term, index) => {
                const sanitizedTerm = term.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
                const minLengthTerm = sanitizedTerm.length >= 2 ? sanitizedTerm : "";
                return minLengthTerm
                    ? acc + `${minLengthTerm}${index !== terms.length - 1 ? "|" : ""}`
                    : "";
            }, "")})`,
            "ig"
        );
    const highlightSpan = `<span class="${highlight}">$1</span>`;
    const regexTerm = regex(searchTerms);

    const createRow = (key: string, value: string | null, postfix?: string) => {
        return `
        <div class="${cx("row")}">
            <span class="${cx("key", { showingCombined })}">
                ${key}
            </span>
            <span class="${cx("value", { showingCombined })}">
                ${value}${postfix}
            </span>
        </div>`;
    };

    const createNoDataMessageRow = (noDataMessage: string) => {
        return `
        <div class="${cx("row")}">
            <span class="${cx("key", { showingCombined })}">
            ${noDataMessage}
            </span>
        </div>`;
    };

    const featuresTabComponent = (
        <FAQ
            title=""
            forceOpen
            handleCustomOpen={customOnClick}
            contentWrapperClassName={cx({
                withSearchTermsContentWrapper: showingCombined,
            })}
            clearBackground
            isStructuredData={false}
            questions={orderedCarFeaturesCategories.reduce((acc, data) => {
                if (!data?.data?.length) {
                    if (!data?.noSpecsAvailableMessage?.length) {
                        return acc;
                    }
                }

                const sortedOrderedData = data.orderedData.sort(function (a, b) {
                    if (data.rowTitle == "Service history" && data.orderedData.length > 1) {
                        var dateA = dayjs(a.key, "DD/MM/YYYY");
                        var dateB = dayjs(b.key, "DD/MM/YYYY");

                        if (dateA.isValid() || dateB.isValid()) {
                            return dateA.isAfter(dateB) ? -1 : dateA.isBefore(dateB) ? 1 : 0;
                        }
                    }

                    var textA = a.value?.toUpperCase();
                    var textB = b.value?.toUpperCase();
                    if (!textA || !textB) return 0;

                    return textA < textB ? -1 : textA > textB ? 1 : 0;
                });

                let html = sortedOrderedData.reduce((htmlAcc, spec) => {
                    const valueTest = regexTerm.test(spec.value ?? "");
                    const value = spec.value?.replace(regexTerm, highlightSpan);

                    const weHaveMatches = valueTest;
                    const searchTermsArePresent = searchTerms.length;

                    if (
                        searchTermsArePresent &&
                        weHaveMatches &&
                        searchTermCombinedIsAtLeast2Chars
                    ) {
                        return htmlAcc + `<p>${value}</p>`;
                    }

                    if (!searchTermsArePresent || !searchTermCombinedIsAtLeast2Chars) {
                        return htmlAcc + `<p>${spec.value}</p>`;
                    }

                    return htmlAcc;
                }, "");

                if (!html && !(sortedOrderedData.length > 0) && data.noSpecsAvailableMessage) {
                    const noSpecsAvailableTest = regexTerm.test(data.noSpecsAvailableMessage);
                    const noSpecsAvailable = data.noSpecsAvailableMessage.replace(
                        regexTerm,
                        highlightSpan
                    );

                    if (
                        searchTerms.length &&
                        noSpecsAvailableTest &&
                        searchTermCombinedIsAtLeast2Chars
                    ) {
                        html = createNoDataMessageRow(noSpecsAvailable);
                    }

                    if (!searchTerms.length || !searchTermCombinedIsAtLeast2Chars) {
                        html = createNoDataMessageRow(data.noSpecsAvailableMessage);
                    }
                }

                return [
                    ...acc,
                    {
                        className: !html ? cx("hide") : undefined,
                        openByDefault:
                            (data.selectedRow === selectedRow && !dirtyItemsRef.current) ||
                            !!openedItems.find((item) => item === data.rowTitle),
                        question: data.rowTitle,
                        openAll: showingCombined && !!html,
                        anchor: `${data.rowTitle.replaceAll(" ", "").toLowerCase()}`,
                        answer: {
                            html,
                            resolvedItems: [],
                        },
                        onCloseFaqItem: (itemName: string) => {
                            dirtyItemsRef.current = true;
                            setOpenedItems(openedItems.filter((item) => item !== itemName));
                        },
                        onOpenFaqItem: (itemName: string) => {
                            dirtyItemsRef.current = true;
                            setOpenedItems([...openedItems, itemName]);
                        },
                    },
                ];
            }, [] as QaProps[])}
        />
    );

    const specsTabComponent = (
        <FAQ
            title=""
            forceOpen
            handleCustomOpen={customOnClick}
            contentWrapperClassName={cx({
                withSearchTermsContentWrapper: showingCombined,
            })}
            clearBackground
            isStructuredData={false}
            questions={orderedCarSpecsCategories.reduce((acc, data) => {
                if (!data?.data?.length) {
                    if (!data?.noSpecsAvailableMessage?.length) {
                        return acc;
                    }
                }

                const sortedOrderedData = data.orderedData.sort(function (a, b) {
                    if (data.rowTitle == "Service history" && data.orderedData.length > 1) {
                        var dateA = dayjs(a.key, "DD/MM/YYYY");
                        var dateB = dayjs(b.key, "DD/MM/YYYY");

                        if (dateA.isValid() || dateB.isValid()) {
                            return dateA.isAfter(dateB) ? -1 : dateA.isBefore(dateB) ? 1 : 0;
                        }
                    }

                    var textA = a.key?.toUpperCase();
                    var textB = b.key?.toUpperCase();

                    return textA < textB ? -1 : textA > textB ? 1 : 0;
                });

                let html = sortedOrderedData.reduce((htmlAcc, spec) => {
                    const keyTest = regexTerm.test(spec.key);
                    const valueTest = regexTerm.test(spec.value ?? "");
                    const postFixTest = regexTerm.test(spec.postfix);

                    const key = spec.key.replace(regexTerm, highlightSpan);
                    const value = spec.value?.replace(regexTerm, highlightSpan);
                    const postFix = spec.postfix?.replace(regexTerm, highlightSpan);

                    const weHaveMatches = keyTest || valueTest || postFixTest;

                    const searchTermsArePresent = searchTerms.length;

                    if (
                        searchTermsArePresent &&
                        weHaveMatches &&
                        searchTermCombinedIsAtLeast2Chars
                    ) {
                        return htmlAcc + createRow(key, value ?? "", postFix);
                    }

                    if (!searchTermsArePresent || !searchTermCombinedIsAtLeast2Chars) {
                        return htmlAcc + createRow(spec.key, spec.value ?? "", spec?.postfix ?? "");
                    }

                    return htmlAcc;
                }, "");

                if (!html && !(sortedOrderedData.length > 0) && data.noSpecsAvailableMessage) {
                    const noSpecsAvailableTest = regexTerm.test(data.noSpecsAvailableMessage);
                    const noSpecsAvailable = data.noSpecsAvailableMessage.replace(
                        regexTerm,
                        highlightSpan
                    );

                    if (
                        searchTerms.length &&
                        noSpecsAvailableTest &&
                        searchTermCombinedIsAtLeast2Chars
                    ) {
                        html = createNoDataMessageRow(noSpecsAvailable);
                    }

                    if (!searchTerms.length || !searchTermCombinedIsAtLeast2Chars) {
                        html = createNoDataMessageRow(data.noSpecsAvailableMessage);
                    }
                }

                return [
                    ...acc,
                    {
                        className: !html ? cx("hide") : undefined,
                        openByDefault:
                            (data.selectedRow.replaceAll(" ", "").toLowerCase() ===
                                selectedRow.replaceAll(" ", "").toLowerCase() &&
                                !dirtyItemsRef.current) ||
                            !!openedItems.find((item) => item === data.rowTitle),
                        question: data.rowTitle,
                        openAll: showingCombined && !!html,
                        anchor: `${data.rowTitle.replaceAll(" ", "").toLowerCase()}`,
                        answer: {
                            html,
                            resolvedItems: [],
                        },
                        onCloseFaqItem: (itemName: string) => {
                            dirtyItemsRef.current = true;
                            setOpenedItems(openedItems.filter((item) => item !== itemName));
                        },
                        onOpenFaqItem: (itemName: string) => {
                            dirtyItemsRef.current = true;
                            setOpenedItems([...openedItems, itemName]);
                        },
                    },
                ];
            }, [] as QaProps[])}
        />
    );

    const jointContent = (
        <>
            {specsTabComponent}
            {featuresTabComponent}
        </>
    );

    const tabs: TabsData = [
        {
            component:
                searchTerms?.length && searchTermCombinedIsAtLeast2Chars
                    ? jointContent
                    : featuresTabComponent,
            tabName: vehicleDetailsProps.carFeatures.title,
        },
        {
            component:
                searchTerms?.length && searchTermCombinedIsAtLeast2Chars
                    ? jointContent
                    : specsTabComponent,
            tabName: vehicleDetailsProps.carSpecs.title,
        },
    ];

    return (
        <>
            <Wrapper className={cx("wrapper")}>
                <div className={cx("search")}>
                    <TextInput
                        placeholder="Search for..."
                        icon="Search"
                        label="Search for"
                        {...register("search")}
                        value={searchValue}
                    />
                    {!!searchTerms.length && (
                        <button
                            onClick={handleResetSearch}
                            className={cx("button", "closeButton")}
                            title="Close Modal"
                            aria-label="Close Modal"
                        >
                            <UIIcon icon="Close" />
                        </button>
                    )}
                </div>
            </Wrapper>

            {showingCombined && (
                <p className={cx("showingCombinedText")}>Matching vehicle specs or features:</p>
            )}
            <Tabs
                tabs={tabs}
                className={cx("tabsLiveArea", {
                    withSearchTerms: showingCombined,
                })}
                tabsWrapperClassname={cx("tabsWrapper")}
                tabClassName={cx("tab")}
                activeTabClassName={cx("activeTab")}
                tabNameDefaultSelected={tabNameDefaultSelected}
                hideTabs={showingCombined}
            />
        </>
    );
};

export default FeaturesSpecsModal;
